import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigationType } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { CommonResponsesProvider } from "@/context/CommonResponsesContext";
import { LanguageProvider } from "@/context/LanguageContext";
import CommonResponsesSidebar from "@/components/CommonResponsesSidebar";
import StepUpDialog from "@/components/StepUpDialog";
import FirmAgentDrawer from "@/components/FirmAgentDrawer";
import { SessionTimeoutGuard } from "@/components/SessionTimeoutGuard";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";

const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Contact = lazy(() => import("@/pages/Contact"));
const Security = lazy(() => import("@/pages/Security"));
const Intake = lazy(() => import("@/pages/Intake"));
const About = lazy(() => import("@/pages/About"));
const Status = lazy(() => import("@/pages/Status"));
const Changelog = lazy(() => import("@/pages/Changelog"));
const AdminMetrics = lazy(() => import("@/pages/AdminMetrics"));
const AdminSegmentDetail = lazy(() => import("@/pages/AdminSegmentDetail"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const ConsultantDashboard = lazy(() => import("@/pages/ConsultantDashboard"));
const ClientWorkspace = lazy(() => import("@/pages/ClientWorkspace"));
const VerifyAuditLog = lazy(() => import("@/pages/VerifyAuditLog"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const IntegrationsDocs = lazy(() => import("@/pages/IntegrationsDocs"));
const AuditorDashboard = lazy(() => import("@/pages/AuditorDashboard"));
const MfaSetup = lazy(() => import("@/pages/MfaSetup"));
const Privacy = lazy(() => import("@/pages/Privacy"));

import { ConsentGate } from "@/components/CookieConsent";

const SITE_URL = "https://auditvanguard.com";

const DEFAULT_SEO = {
    title: "GSGD Field Solutions LLC — AuditVanguard GRC Platform",
    description: "GSGD Field Solutions LLC — Secure Systems. Defensible Results. AuditVanguard is the compliance workspace for consultants and their clients.",
};

const SEO_BY_PATH = {
    "/": {
        title: "AuditVanguard — Compliance & GRC Platform for Consultants | GSGD Field Solutions LLC",
        description: "AuditVanguard collects audit evidence automatically from Okta, AWS, and GitHub and maps it to your SOC 2, ISO 27001, PCI DSS, and HIPAA controls.",
    },
    "/login": {
        title: "Sign In | AuditVanguard by GSGD Field Solutions LLC",
        description: "Sign in to your AuditVanguard workspace. Consultants, clients and auditors use one secure door — we route you to your own space.",
    },
    "/register": {
        title: "Create Your Consultant Account | AuditVanguard",
        description: "Start managing compliance engagements in minutes. Create your AuditVanguard consultant account — no credit card required to begin.",
    },
    "/forgot-password": {
        title: "Reset Your Password | AuditVanguard",
        description: "Request a secure password reset link for your AuditVanguard account.",
    },
    "/reset-password": {
        title: "Choose a New Password | AuditVanguard",
        description: "Set a new password for your AuditVanguard account using your secure reset link.",
    },
    "/pricing": {
        title: "Pricing & Plans | AuditVanguard",
        description: "Simple, transparent pricing for compliance consultants. Start free, then scale with the Firm plan — unlimited clients, AI assistance and branded reports.",
    },
    "/contact": {
        title: "Contact Us | AuditVanguard by GSGD Field Solutions LLC",
        description: "Talk to GSGD Field Solutions LLC about compliance engagements, partnerships or support. We respond within one business day.",
    },
    "/security": {
        title: "Security & Trust Center | AuditVanguard",
        description: "How we protect your data: httpOnly session cookies, field-level encryption, hash-chained audit logs, MFA and SOC 2-aligned controls.",
    },
    "/intake": {
        title: "Client Intake Form | AuditVanguard",
        description: "Zero-touch onboarding for new compliance clients. Share your details once and your consultant's workspace is prepared automatically.",
    },
    "/about": {
        title: "About Our Team | GSGD Field Solutions LLC",
        description: "Meet the team behind AuditVanguard — security practitioners and GRC advisors helping firms reach defensible compliance outcomes.",
    },
    "/status": {
        title: "System Status & Uptime | AuditVanguard",
        description: "Live operational status for AuditVanguard: API health, 90-day incident history and the fleet-wide audit-chain integrity anchor.",
    },
    "/changelog": {
        title: "Changelog & Release Notes | AuditVanguard",
        description: "Every AuditVanguard release, on the record — new features, security hardening and fixes shipped continuously by GSGD Field Solutions LLC.",
    },
    "/verify-audit-log": {
        title: "Verify Audit Log Integrity | AuditVanguard",
        description: "Independently verify the hash-chained integrity of an exported AuditVanguard audit log.",
    },
    "/privacy": {
        title: "Privacy Policy | AuditVanguard by GSGD Field Solutions LLC",
        description: "How AuditVanguard handles your data: the cookies we set, what we collect, and the choices you have. No tracking in the logged-in workspace.",
    },
};

const PRIVATE_PREFIXES = ["/dashboard", "/clients", "/portal", "/auditor", "/admin", "/payment", "/mfa-setup"];

function setMeta(attr, key, content) {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function RouteSeo() {
    const { pathname } = useLocation();
    useEffect(() => {
        const seo = SEO_BY_PATH[pathname] || DEFAULT_SEO;
        document.title = seo.title;
        setMeta("name", "title", seo.title);
        setMeta("name", "description", seo.description);
        setMeta("property", "og:title", seo.title);
        setMeta("property", "og:description", seo.description);
        setMeta("property", "og:url", `${SITE_URL}${pathname}`);
        setMeta("name", "twitter:title", seo.title);
        setMeta("name", "twitter:description", seo.description);

        const isPrivate = PRIVATE_PREFIXES.some((p) => pathname.startsWith(p));
        setMeta("name", "robots", isPrivate ? "noindex, nofollow" : "index, follow");

        let link = document.head.querySelector('link[rel="canonical"]');
        if (SEO_BY_PATH[pathname]) {
            if (!link) {
                link = document.createElement("link");
                link.setAttribute("rel", "canonical");
                document.head.appendChild(link);
            }
            link.setAttribute("href", `${SITE_URL}${pathname}`);
        } else if (link) {
            link.remove();
        }
    }, [pathname]);
    return null;
}

// Reset scroll on every NEW navigation (push/replace) so pages open at the top.
// - Hash-only changes are skipped: in-page anchors (#templates, #main-content)
//   and the skip link keep their native behaviour.
// - POP (browser Back/Forward) is skipped so the browser restores the previous
//   scroll position.
// - The jump is instant (no smooth scrolling), which also satisfies
//   prefers-reduced-motion.
// - Focus moves to #main-content so keyboard/screen-reader users start at the top.
function ScrollToTop() {
    const { pathname, hash } = useLocation();
    const navType = useNavigationType();
    useEffect(() => {
        if (hash) {
            const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
            return () => clearTimeout(t);
        }
        if (navType === "POP") return;
        window.scrollTo(0, 0);
        const main = document.getElementById("main-content");
        if (main) {
            main.setAttribute("tabindex", "-1");
            main.focus({ preventScroll: true });
        }
    }, [pathname, hash, navType]);
    return null;
}

function RootRedirect() {
    const { user, ready } = useAuth();
    if (!ready) return null;
    if (!user) return <Landing />;
    if (user.role === "client") return <Navigate to="/portal" replace />;
    if (user.role === "auditor") return <Navigate to="/auditor" replace />;
    return <Navigate to="/dashboard" replace />;
}

const TOAST_OPTIONS = {
    classNames: {
        toast: "bg-card border-border text-foreground font-display",
    },
};

function PageFallback() {
    return <div className="min-h-screen bg-background" aria-busy="true" />;
}

function App() {
    return (
        <LanguageProvider>
        <ThemeProvider>
            <AuthProvider>
                <CommonResponsesProvider>
                <BrowserRouter>
                    <a
                        href="#main-content"
                        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:bg-background focus:text-foreground focus:px-4 focus:py-2 focus:rounded-md focus:border focus:border-border"
                        data-testid="skip-to-content"
                    >
                        Skip to content
                    </a>
                    <div className="App">
                        <RouteSeo />
                        <ScrollToTop />
                        <Suspense fallback={<PageFallback />}>
                        <Routes>
                            <Route path="/" element={<RootRedirect />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={<Register />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                            <Route path="/pricing" element={<Pricing />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/security" element={<Security />} />
                            <Route path="/docs/integrations" element={<IntegrationsDocs />} />
                            <Route path="/intake" element={<Intake />} />
                            <Route path="/intake/:token" element={<Intake />} />
                            <Route path="/about" element={<About />} />
                            <Route
                                path="/status"
                                element={
                                    <ProtectedRoute adminOnly>
                                        <Status />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/changelog"
                                element={<Changelog />}
                            />
                            <Route path="/verify-audit-log" element={<VerifyAuditLog />} />
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="/verify-email" element={<VerifyEmail />} />
                            <Route path="/admin" element={<Navigate to="/admin/metrics" replace />} />
                            <Route
                                path="/admin/metrics"
                                element={
                                    <ProtectedRoute adminOnly>
                                        <AdminMetrics />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/metrics/:segment"
                                element={
                                    <ProtectedRoute adminOnly>
                                        <AdminSegmentDetail />
                                    </ProtectedRoute>
                                }
                            />
                            <Route path="/payment/success" element={<PaymentSuccess />} />
                            <Route path="/payment/cancel" element={<PaymentCancel />} />
                            <Route
                                path="/dashboard"
                                element={
                                    <ProtectedRoute role="consultant">
                                        <ConsultantDashboard />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/mfa-setup"
                                element={
                                    <ProtectedRoute>
                                        <MfaSetup />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/clients/:clientId"
                                element={
                                    <ProtectedRoute>
                                        <ClientWorkspace />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/portal"
                                element={
                                    <ProtectedRoute role="client">
                                        <ClientWorkspace portalMode />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/auditor"
                                element={
                                    <ProtectedRoute role="auditor">
                                        <AuditorDashboard />
                                    </ProtectedRoute>
                                }
                            />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                        </Suspense>
                        <ConsentGate />
                        <Toaster
                            position="top-right"
                            toastOptions={TOAST_OPTIONS}
                        />
                        <CommonResponsesSidebar />
                        <StepUpDialog />
                        <FirmAgentDrawer />
                        <SessionTimeoutGuard />
                    </div>
                </BrowserRouter>
                </CommonResponsesProvider>
            </AuthProvider>
        </ThemeProvider>
        </LanguageProvider>
    );
}

export default App;
